/**
 * Check if code is running on client-side (browser)
 * @returns boolean
 */
export function isClient() {
  return typeof window !== 'undefined';
}

/**
 * Check if current device is mobile based on screen width
 * @returns boolean
 */
export function isMobile() {
  if (!isClient()) return false;
  
  // Check if the viewport width is less than 768px (typical tablet breakpoint)
  return window.innerWidth < 768;
}

/**
 * Check if current device is tablet based on screen width
 * @returns boolean
 */
export function isTablet() {
  if (!isClient()) return false;
  
  // Check if the viewport width is between 768px and 1024px
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

/**
 * Check if current device is desktop based on screen width
 * @returns boolean
 */
export function isDesktop() {
  if (!isClient()) return false;
  
  // Check if the viewport width is 1024px or greater
  return window.innerWidth >= 1024;
}

/**
 * Check if current device has touch capabilities
 * @returns boolean
 */
export function isTouchDevice() {
  if (!isClient()) return false;
  
  // Check if the device has touch capabilities
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 || 
         (navigator as any).msMaxTouchPoints > 0;
}

/**
 * Get device and browser information from user agent
 * @returns Object with device information
 */
export function getUserDevice() {
  if (!isClient()) {
    return null;
  }

  const userAgent = navigator.userAgent;
  let browser = 'Unknown';
  let deviceType = 'Unknown';
  let browserVersion = 'Unknown';

  // Detect browser and version
  if (userAgent.indexOf('SamsungBrowser') > -1) {
    browser = 'Samsung Browser';
    const match = userAgent.match(/SamsungBrowser\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Chrome') > -1) {
    browser = 'Chrome';
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Firefox') > -1) {
    browser = 'Firefox';
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Safari') > -1) {
    browser = 'Safari';
    const match = userAgent.match(/Version\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg/') > -1) {
    browser = 'Edge';
    const match = userAgent.match(/Edge\/([0-9.]+)/) || userAgent.match(/Edg\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) {
    browser = 'Internet Explorer';
    const match = userAgent.match(/(?:MSIE |rv:)([0-9.]+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browser = 'Opera';
    const match = userAgent.match(/(?:Opera|OPR)\/([0-9.]+)/);
    if (match) browserVersion = match[1];
  }

  // Detect device type
  if (/ipad|tablet|playbook|silk|android(?!.*mobile)/i.test(userAgent.toLowerCase())) {
    deviceType = 'Tablet';
  } else if (/mobile|iphone|ipod|blackberry|android|palm|windows phone/i.test(userAgent.toLowerCase())) {
    deviceType = 'Mobile';
  } else {
    deviceType = 'Desktop';
  }

  return {
    browser,
    browserVersion,
    deviceType,
    userAgent,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    language: navigator.language,
    hasTouch: isTouchDevice(),
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    prefersDarkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
  };
}